import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Team } from 'api/models';
import {
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import { resourceIdForTeamAsLeaf } from 'components/organisms/resource-management/utils/id';
import {
  AllocatedMembers,
  saveTemplatePlaceholder,
} from 'data/templates/projects';
import { updateAllocatedMembersAction } from 'data/templates/projects/actions';

interface Props {
  schedulerRef: SchedulerReference;
  allocatedMembers: AllocatedMembers;
  project: ProjectsWithMembers;
}
interface Result {
  editTeamModalTeamId?: number;
  showEditTeamModal: (teamId: number) => void;
  hideEditTeamModal: () => void;
  onTeamEdit: (team: Team) => void;
}
export const useEditTeamsManager = ({
  schedulerRef,
  allocatedMembers,
  project,
}: Props): Result => {
  const dispatch = useDispatch();
  const [teamIdToBeUpdated, setTeamIdToBeUpdated] = useState<number>();
  const showEditTeamModal = useCallback((teamId: number) => {
    setTeamIdToBeUpdated(teamId);
  }, []);
  const hideEditTeamModal = useCallback(() => {
    setTeamIdToBeUpdated(undefined);
  }, []);
  const replaceTeamInTheState = useCallback(
    (team: Team) => {
      dispatch(
        updateAllocatedMembersAction({
          users: allocatedMembers.users,
          teams: allocatedMembers.teams
            .filter((x) => x.teamId !== team.teamId)
            .concat(team),
        }),
      );
      dispatch(saveTemplatePlaceholder(team));
    },
    [allocatedMembers.teams, allocatedMembers.users, dispatch],
  );
  const onTeamEdit = useCallback(
    (team: Team) => {
      const { teamId, teamName, roleId } = team;

      if (roleId != null) {
        const resource = schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(project.projectId, teamId),
        );

        resource.set('teamName', teamName);
      }
      replaceTeamInTheState(team);
      hideEditTeamModal();
    },
    [hideEditTeamModal, project.projectId, replaceTeamInTheState, schedulerRef],
  );

  return {
    editTeamModalTeamId: teamIdToBeUpdated,
    showEditTeamModal,
    hideEditTeamModal,
    onTeamEdit,
  };
};
